import React, { useEffect, useState, useCallback } from 'react';
import { supabase } from '../supabaseClient';

// Cache aal2 confirmation for the lifetime of the browser session so re-navigating
// to the admin panel doesn't flash a black loading screen on every visit.
let _mfaOkThisSession = false;

// Admin-only MFA gate.
// 1. If admin has no verified TOTP factor → show enrollment (QR + verify).
// 2. If admin has factor but session is aal1 → show challenge (enter code).
// 3. Once aal2 reached → render children.
export default function AdminMfaGate({ children }) {
  const [state, setState] = useState(_mfaOkThisSession ? 'ok' : 'loading'); // loading | enroll | challenge | ok
  const [enroll, setEnroll] = useState(null);    // { factorId, qr, secret }
  const [challengeId, setChallengeId] = useState(null);
  const [factorId, setFactorId] = useState(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    const { data: aalData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    const { data: factorsData } = await supabase.auth.mfa.listFactors();
    const verified = (factorsData?.totp || []).filter(f => f.status === 'verified');

    if (verified.length === 0) {
      const { data: enrollData, error: enrollErr } = await supabase.auth.mfa.enroll({ factorType: 'totp' });
      if (enrollErr) { setError(enrollErr.message); return; }
      setEnroll({
        factorId: enrollData.id,
        qr: enrollData.totp.qr_code,
        secret: enrollData.totp.secret,
      });
      setState('enroll');
      return;
    }

    if (aalData?.currentLevel === 'aal2') {
      _mfaOkThisSession = true;
      setState('ok');
      return;
    }

    const fId = verified[0].id;
    setFactorId(fId);
    const { data: ch, error: chErr } = await supabase.auth.mfa.challenge({ factorId: fId });
    if (chErr) { setError(chErr.message); return; }
    setChallengeId(ch.id);
    setState('challenge');
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const verifyEnroll = async () => {
    if (!enroll || !code) return;
    setBusy(true); setError(null);
    const { data: ch, error: chErr } = await supabase.auth.mfa.challenge({ factorId: enroll.factorId });
    if (chErr) { setError(chErr.message); setBusy(false); return; }
    const { error: vErr } = await supabase.auth.mfa.verify({
      factorId: enroll.factorId,
      challengeId: ch.id,
      code: code.trim(),
    });
    setBusy(false);
    if (vErr) { setError(vErr.message); return; }
    _mfaOkThisSession = true;
    setCode(''); setEnroll(null);
    await refresh();
  };

  const verifyChallenge = async () => {
    if (!challengeId || !factorId || !code) return;
    setBusy(true); setError(null);
    const { error: vErr } = await supabase.auth.mfa.verify({
      factorId, challengeId, code: code.trim(),
    });
    setBusy(false);
    if (vErr) { setError(vErr.message); return; }
    _mfaOkThisSession = true;
    setCode('');
    await refresh();
  };

  if (state === 'ok') return children;

  const wrapStyle = {
    minHeight: '100vh', background: '#000', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 24,
  };
  const cardStyle = {
    maxWidth: 460, width: '100%',
    background: 'rgba(20,20,20,0.85)',
    border: '1px solid rgba(157,21,21,0.4)',
    padding: 32, borderRadius: 4,
    fontFamily: "'Inter', system-ui, sans-serif",
  };
  const titleStyle = {
    fontFamily: "'Bebas Neue','Oswald',sans-serif",
    fontSize: 28, letterSpacing: '0.08em',
    margin: '0 0 8px', color: '#fff',
  };
  const subStyle = { fontSize: 13, color: '#888', margin: '0 0 24px', lineHeight: 1.5 };
  const inputStyle = {
    width: '100%', padding: 12, fontSize: 18, letterSpacing: '0.3em',
    textAlign: 'center', background: '#0a0a0a',
    border: '1px solid #333', color: '#fff',
    fontFamily: "'JetBrains Mono', monospace",
    borderRadius: 2, boxSizing: 'border-box',
  };
  const btnStyle = {
    width: '100%', padding: 14, marginTop: 16,
    background: '#9D1515', color: '#fff', border: 'none',
    fontFamily: "'Bebas Neue','Oswald',sans-serif",
    fontSize: 16, letterSpacing: '0.1em', cursor: 'pointer',
    borderRadius: 2, opacity: busy ? 0.6 : 1,
  };
  const errStyle = { color: '#ff6b6b', fontSize: 13, marginTop: 12 };

  if (state === 'loading') {
    return <div style={wrapStyle}><div style={cardStyle}><p style={subStyle}>Checking admin security…</p></div></div>;
  }

  if (state === 'enroll' && enroll) {
    return (
      <div style={wrapStyle}>
        <div style={cardStyle}>
          <h1 style={titleStyle}>ENABLE 2FA</h1>
          <p style={subStyle}>
            Admin accounts require two-factor authentication. Scan this QR code with an
            authenticator app (Google Authenticator, 1Password, Authy), then enter the 6-digit code.
          </p>
          <div style={{ background: '#fff', padding: 16, borderRadius: 2, display: 'flex', justifyContent: 'center' }}>
            <img src={enroll.qr} alt="2FA QR Code" style={{ width: 200, height: 200 }} />
          </div>
          <p style={{ ...subStyle, marginTop: 16, fontSize: 11 }}>
            Can't scan? Manual key:{' '}
            <code style={{ color: '#9D1515', fontFamily: "'JetBrains Mono',monospace", wordBreak: 'break-all' }}>
              {enroll.secret}
            </code>
          </p>
          <input
            style={inputStyle}
            type="text" inputMode="numeric" maxLength={6} placeholder="000000"
            value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
            autoFocus
          />
          {error && <div style={errStyle}>{error}</div>}
          <button style={btnStyle} onClick={verifyEnroll} disabled={busy || code.length < 6}>
            {busy ? 'VERIFYING…' : 'VERIFY & ENABLE'}
          </button>
        </div>
      </div>
    );
  }

  if (state === 'challenge') {
    return (
      <div style={wrapStyle}>
        <div style={cardStyle}>
          <h1 style={titleStyle}>2FA REQUIRED</h1>
          <p style={subStyle}>Enter the 6-digit code from your authenticator app to access the admin panel.</p>
          <input
            style={inputStyle}
            type="text" inputMode="numeric" maxLength={6} placeholder="000000"
            value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
            onKeyDown={e => { if (e.key === 'Enter' && code.length === 6) verifyChallenge(); }}
            autoFocus
          />
          {error && <div style={errStyle}>{error}</div>}
          <button style={btnStyle} onClick={verifyChallenge} disabled={busy || code.length < 6}>
            {busy ? 'VERIFYING…' : 'VERIFY'}
          </button>
        </div>
      </div>
    );
  }

  return <div style={wrapStyle}><div style={cardStyle}><p style={errStyle}>{error || 'MFA error'}</p></div></div>;
}
